// ─── Pelago 1 substance category → Galapagos program key ────────────────────
//
// Pelago 1 keys an order by a free-text `substanceCategory`; Galapagos keys it
// by `member_orders.member_program_id`, an FK to the member's enrollment. This
// mapping is what lets a later import resolve one to the other, and it is the
// reason `member_program_id` can be NOT NULL: every category a legacy order
// can carry resolves to a program key that exists in the catalog.
//
// Two categories collapse onto `tobacco`. That is lossless here — the
// cigarettes/e-cigarettes distinction lives in the programme's own
// `config.intake.subtypes`, and nothing about an order depends on it.
//
// The key set is the Pelago 1 vocabulary as it stands. It is duplicated in the
// Atlas client contract (`apps/atlas/src/services/api/types/member.types.ts`),
// which a package cannot import from (ADR-001); a parity test on the Atlas
// side asserts the two agree rather than either standing alone.

/**
 * The Pelago 1 category vocabulary. Declared as the source array rather than
 * derived from the mapping's keys, so it can type the mapping below — and so
 * the Atlas parity assertion has something to compare against that does not
 * depend on the mapping being correct.
 */
export const BACKEND2_SUBSTANCE_CATEGORIES = [
  'cigarettes',
  'eCigarettes',
  'alcohol',
  'opioids',
  'cannabis',
  'stimulants',
] as const;

export type Backend2SubstanceCategory = (typeof BACKEND2_SUBSTANCE_CATEGORIES)[number];

/**
 * Every Pelago 1 `substanceCategory` value, mapped to the Galapagos program
 * catalog key that replaces it. A category with no entry here would be an
 * order the import cannot place.
 *
 * The `satisfies` clause makes that totality a compile error rather than
 * something a test has to notice: adding a category to the vocabulary above
 * without a mapping entry fails `tsc`. The runtime guard covers the half types
 * cannot see — whether each target key is actually seeded in the catalog.
 */
export const BACKEND2_SUBSTANCE_CATEGORY_TO_PROGRAM_KEY = {
  cigarettes: 'tobacco',
  eCigarettes: 'tobacco',
  alcohol: 'alcohol',
  opioids: 'opioid',
  cannabis: 'cannabis',
  stimulants: 'stimulants',
} as const satisfies Readonly<Record<Backend2SubstanceCategory, string>>;
